import Modal from "@/components/Modal";
import DatePicker from "@/components/form/DatePicker";
import Input from "@/components/form/Input";
import { useAppDispatch } from "@/hooks";
import { updateProject } from "@/store/slices/projectSlice";
import { ProjectList } from "@/types/project.model";
import moment from "moment";
import React from "react";
import { useForm } from "react-hook-form";

interface ExtendEndDateFormData {
  proposedEndDate: string;
}

interface ExtendEndDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: ProjectList;
  isLoading?: boolean;
}

const ExtendEndDateModal: React.FC<ExtendEndDateModalProps> = ({
  isOpen,
  onClose,
  project,
  isLoading = false,
}) => {
  // Initialize form
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm<ExtendEndDateFormData>({
    defaultValues: {
      proposedEndDate: "",
    },
    mode: "onChange",
  });

  const dispatch = useAppDispatch();

  // Form submission handler
  const onFormSubmit = (data: ExtendEndDateFormData) => {
    const updatedProject: any = {
      // ...project,
      id: project?.id,
      client_id: project.client.id,
      start_date: moment(project.start_date, "DD/MM/YYYY").format("DDMMYYYY"),
      end_date: moment(data.proposedEndDate, "YYYY-MM-DD").format("DDMMYYYY"),
    };

    delete updatedProject.client;

    dispatch(updateProject(updatedProject))
      .unwrap()
      .then(() => {
        onModalClose();
      });
  };

  const onModalClose = () => {
    onClose();
    setValue("proposedEndDate", "");
  };

  const proposedEndDate = watch("proposedEndDate");
  return (
    <Modal
      isOpen={isOpen}
      onClose={onModalClose}
      title="Extend end date"
      width="min(95%, 868px)"
      contentClassName="p-0"
    >
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="px-3 sm:px-4 py-2">
          <p className="text-[#6C6C6C] text-[14px] mb-4">
            <span className="text-[#DC2626]">*</span>Indicates mandatory fields
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <div>
              <Input
                label="Current end date"
                type="text"
                value={
                  project.end_date
                    ? moment(project.end_date, "DD/MM/YYYY").format(
                        "MM/DD/YYYY"
                      )
                    : ""
                }
                readOnly
              />
            </div>
            <div>
              <DatePicker
                label="Proposed end date"
                required
                defaultMonth={moment(project.end_date, "DD/MM/YYYY").format(
                  "YYYY-MM-DD"
                )}
                value={proposedEndDate}
                onChange={(value) => {
                  setValue("proposedEndDate", value);
                  trigger("proposedEndDate");
                }}
                minDate={moment(project.end_date, "DD/MM/YYYY").format(
                  "YYYY-MM-DD"
                )}
                placeholder="Select proposed end date"
                error={errors.proposedEndDate?.message}
              />
              <input
                type="hidden"
                {...register("proposedEndDate", {
                  required: "Proposed end date is required",
                })}
              />
            </div>
          </div>
        </div>
        <div className="px-3 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 rounded-b-[10px]">
          <button
            type="button"
            onClick={onModalClose}
            className="px-4 py-2 text-[14px] w-full sm:w-[147px] text-gray-700 bg-[#F5F5F5] rounded-md hover:bg-gray-50 cursor-pointer order-1 sm:order-none"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-full sm:w-[147px] h-[42px] bg-[#73b5c5] text-white text-[14px] font-[500] border-none rounded-[6px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed mb-2 sm:mb-0"
            disabled={!isValid || isLoading}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Updating...
              </>
            ) : (
              "Update end date"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ExtendEndDateModal;
